import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PopupWrapper from '../../../../components/popup/PopupWrapper';
import AppDatePicker from '../../../../components/widgets/AppDatePicker';
import { getButtonClass } from '../../../../components/popup/popupUtils';
import { formatAllianceMemberRoleLabel } from '../alli/members/allianceMemberRoleLabels';

const TEMP_PERMISSION_KEYS = [
  { key: 'invite', labelKey: 'popup.memberTempPermission.permInvite' },
  { key: 'roleChange', labelKey: 'popup.memberTempPermission.permRoleChange' },
  { key: 'deleteDoc', labelKey: 'popup.memberTempPermission.permDeleteDoc' },
];

/**
 * 임시 권한 설정 공통 팝업
 * config.initialData: { member }
 * onClose(submit, { checks, start, end })
 */
const MemberTempPermissionPopup = ({ config, onClose }) => {
  const { t } = useTranslation();
  const { member, tempPermission } = config?.initialData || {};
  const [checks, setChecks] = useState({
    invite: String(tempPermission?.permInviteYn || 'N').toUpperCase() === 'Y',
    roleChange: String(tempPermission?.permRoleChangeYn || 'N').toUpperCase() === 'Y',
    deleteDoc: String(tempPermission?.permDeleteDocYn || 'N').toUpperCase() === 'Y',
  });
  const [start, setStart] = useState(tempPermission?.startDate || '');
  const [end, setEnd] = useState(tempPermission?.endDate || '');

  const handleCancel = () => onClose(false);
  const handleConfirm = () => {
    if (!checks.invite && !checks.roleChange && !checks.deleteDoc) {
      window.alert(t('popup.memberTempPermission.alertSelectPermission'));
      return;
    }
    if (!start || !end) {
      window.alert(t('popup.memberTempPermission.alertEnterDates'));
      return;
    }
    if (start > end) {
      window.alert(t('popup.memberTempPermission.alertEndBeforeStart'));
      return;
    }
    onClose(true, { checks, start, end });
  };

  if (!member) return null;

  return (
    <PopupWrapper
      config={{
        ...config,
        title: t('popup.memberTempPermission.title', {
          name: member.name,
          role: formatAllianceMemberRoleLabel(member.role),
        }),
        width: config?.width ?? 420,
        leftButton: { text: t('popup.common.cancel'), color: 'btn-secondary', submit: false },
        rightButton: { text: t('popup.common.apply'), color: 'btn-primary', submit: true },
      }}
      onClose={onClose}
      displayType={config?.displayType || 'modal'}
      className="popup-member-temp-permission"
    >
      <div className="main-popup-body">
        <p className="small text-muted mb-3">{t('popup.memberTempPermission.subtitle')}</p>
        <p className="small fw-medium mb-2">{t('popup.memberTempPermission.selectPermissions')}</p>
        {TEMP_PERMISSION_KEYS.map(({ key, labelKey }) => (
          <div key={key} className="form-check">
            <input
              type="checkbox"
              id={`temp-${key}`}
              className="form-check-input"
              checked={checks[key]}
              onChange={(e) => setChecks((prev) => ({ ...prev, [key]: e.target.checked }))}
            />
            <label className="form-check-label" htmlFor={`temp-${key}`}>{t(labelKey)}</label>
          </div>
        ))}
        <p className="small fw-medium mt-4 mb-2">{t('popup.memberTempPermission.periodTitle')}</p>
        <div className="row g-2 mb-2">
          <div className="col-6">
            <label className="form-label small mb-0">{t('popup.memberTempPermission.startLabel')}</label>
            <AppDatePicker className="form-control form-control-sm" value={start} onChange={setStart} />
          </div>
          <div className="col-6">
            <label className="form-label small mb-0">{t('popup.memberTempPermission.endLabel')}</label>
            <AppDatePicker className="form-control form-control-sm" value={end} onChange={setEnd} />
          </div>
        </div>
        <p className="small text-muted mb-0">{t('popup.memberTempPermission.expireHint')}</p>
      </div>
      <div className="main-popup-footer d-flex justify-content-between">
        <button type="button" className={`btn main-popup-left-btn ${getButtonClass('btn-secondary')}`} onClick={handleCancel}>
          {t('popup.common.cancel')}
        </button>
        <button type="button" className={`btn main-popup-right-btn ${getButtonClass('btn-primary')}`} onClick={handleConfirm}>
          {t('popup.common.apply')}
        </button>
      </div>
    </PopupWrapper>
  );
};

export default MemberTempPermissionPopup;
